import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface BarraFormatacaoProps {
  valor: string;
  onChange: (novoValor: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

function markdownToHtml(md: string): string {
  const escaped = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
}

function htmlToMarkdown(el: HTMLElement): string {
  function walk(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || '').replace(/ /g, ' ');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const elem = node as HTMLElement;
    const tag = elem.tagName.toLowerCase();
    const inner = Array.from(elem.childNodes).map(walk).join('');
    if (tag === 'strong' || tag === 'b') return `**${inner}**`;
    if (tag === 'em' || tag === 'i') return `*${inner}*`;
    if (tag === 'br') return '\n';
    if (tag === 'div' || tag === 'p') return inner + '\n';
    if (tag === 'span') {
      const fw = elem.style.fontWeight;
      const fs = elem.style.fontStyle;
      if (fw === 'bold' || fw === '700') return `**${inner}**`;
      if (fs === 'italic') return `*${inner}*`;
      return inner;
    }
    return inner;
  }
  const result = Array.from(el.childNodes).map(walk).join('');
  return result.replace(/\n+$/, '');
}

const BarraFormatacao: React.FC<BarraFormatacaoProps> = ({
  valor,
  onChange,
  placeholder = 'Digite aqui...',
  minHeight = 120,
  disabled = false,
  error = false,
  helperText,
}) => {
  const { tema } = useTheme();
  const escuro = tema === 'escuro';
  const editorRef = useRef<HTMLDivElement>(null);
  const lastValorRef = useRef(valor);
  const [boldAtivo, setBoldAtivo] = useState(false);
  const [italicAtivo, setItalicAtivo] = useState(false);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (valor !== lastValorRef.current) {
      lastValorRef.current = valor;
      el.innerHTML = valor ? markdownToHtml(valor) : '';
    }
  }, [valor]);

  const atualizarEstadoBotoes = useCallback(() => {
    setBoldAtivo(document.queryCommandState('bold'));
    setItalicAtivo(document.queryCommandState('italic'));
  }, []);

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    const md = htmlToMarkdown(el);
    lastValorRef.current = md;
    onChange(md);
    atualizarEstadoBotoes();
  };

  const execFormat = (command: 'bold' | 'italic') => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false);
    handleInput();
  };

  const corBorda = escuro ? '#4b5563' : '#d1d5db';
  const borderColor = error ? '#ef4444' : corBorda;

  const estiloBtn = (ativo: boolean): React.CSSProperties => ({
    padding: '3px 10px',
    background: ativo ? '#2563eb' : (escuro ? '#374151' : '#f3f4f6'),
    border: `1px solid ${ativo ? '#1d4ed8' : corBorda}`,
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer',
    color: ativo ? '#ffffff' : (escuro ? '#e5e7eb' : '#374151'),
    lineHeight: 1.4,
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  });

  return (
    <div style={{ position: 'relative' }}>
      {!valor && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 12,
            color: '#9ca3af',
            fontSize: 14,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 1,
          }}
        >
          {placeholder}
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyUp={atualizarEstadoBotoes}
        onMouseUp={atualizarEstadoBotoes}
        style={{
          minHeight,
          padding: '8px 12px',
          border: `1px solid ${borderColor}`,
          borderRadius: 6,
          fontSize: 14,
          lineHeight: 1.6,
          outline: 'none',
          background: disabled ? (escuro ? '#111827' : '#f9fafb') : (escuro ? '#1f2937' : 'white'),
          overflowY: 'auto',
          wordBreak: 'break-word',
          color: escuro ? '#f3f4f6' : '#111827',
        }}
        onFocus={e => {
          e.currentTarget.style.borderColor = '#3b82f6';
          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.2)';
        }}
        onBlur={e => {
          e.currentTarget.style.borderColor = error ? '#ef4444' : corBorda;
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
      {helperText && (
        <p style={{ fontSize: 12, margin: '4px 0 0 0', color: error ? '#ef4444' : '#6b7280' }}>
          {helperText}
        </p>
      )}
      <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center' }}>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execFormat('bold'); }}
          title="Negrito"
          style={{ ...estiloBtn(boldAtivo), fontWeight: 700 }}
        >
          N
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execFormat('italic'); }}
          title="Itálico"
          style={{ ...estiloBtn(italicAtivo), fontStyle: 'italic' }}
        >
          I
        </button>
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>
          Clique em N/I antes de digitar ou selecione o texto e clique
        </span>
      </div>
    </div>
  );
};

export default BarraFormatacao;
